import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "../components/Icon";
import InstallPrompt from "../components/InstallPrompt";
import { useAuth } from "../auth";
import { useI18n } from "../i18n";
import { apiGet } from "../api";
import { getAttractionsCached, pendingCount, syncPending, useIsOnline } from "../offline";
import { formatYmd, docExpiryStatus } from "../dateutil";
import { catIcon, catLabel, CATERING_SESSIONS } from "../constants";
import useTodayChecks, { fmtCheckTime } from "../useTodayChecks";
import { C, R, S } from "../theme";

const rowCard = { display: "flex", alignItems: "center", gap: S.md, background: C.card, borderRadius: R.md, padding: S.md, marginBottom: S.sm, border: `1px solid ${C.border}` };

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const isOnline = useIsOnline();
  const { checks, refresh: refreshChecks } = useTodayChecks();
  const [attractions, setAttractions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [queued, setQueued] = useState(0);
  const [syncingNow, setSyncing] = useState(false);
  const [annualDue, setAnnualDue] = useState([]);
  const [docRenew, setDocRenew] = useState({ soon: 0, expired: 0 });
  const [notice, setNotice] = useState(null);
  const [backOpen, setBackOpen] = useState(false);
  const [backDate, setBackDate] = useState("");
  const [dhOpen, setDhOpen] = useState(false);
  const [cateringPick, setCateringPick] = useState(null);
  const todayStr = new Date().toLocaleDateString("en-CA");
  const minBackStr = new Date(Date.now() - 14 * 86400000).toLocaleDateString("en-CA");
  const catColor = (cat) => (cat === "catering" ? "#f97316" : cat === "inflatable" ? C.blue : C.amber);
  const CATERING_HINTS = { opening: t("dashboard.hintOpening"), service: t("dashboard.hintService"), midday: t("dashboard.hintMidday"), closing: t("dashboard.hintClosing"), weekly: t("dashboard.hintWeekly") };
  const checkable = attractions.filter((a) => a.category !== "catering");
  const doneIds = checkable.filter((a) => checks[a.id]).map((a) => a.id);
  const pendingIds = checkable.filter((a) => !checks[a.id]).map((a) => a.id);
  const todayCount = doneIds.length;

  const load = useCallback(async () => {
    try {
      const a = await getAttractionsCached();
      setAttractions(a);
      const midnight = new Date(); midnight.setHours(0, 0, 0, 0);
      const due = a.filter((x) => x.annual_inspection_date && /^\d{4}-\d{2}-\d{2}$/.test(x.annual_inspection_date))
        .map((x) => { const d = new Date(x.annual_inspection_date + "T00:00:00"); return { id: x.id, name: x.name, days: Math.round((d - midnight) / 86400000) }; })
        .filter((x) => x.days <= 30).sort((p, q) => p.days - q.days);
      setAnnualDue(due);
      await syncPending();
      setQueued(await pendingCount());
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { apiGet("/notice").then(setNotice).catch(() => {}); }, []);

  // Document renewal tracking: count documents expiring soon / expired across all units.
  useEffect(() => {
    apiGet("/documents").then((ds) => {
      let soon = 0, expired = 0;
      (ds || []).forEach((d) => { const st = docExpiryStatus(d.expiry_date); if (st.level === "soon") soon++; else if (st.level === "expired") expired++; });
      setDocRenew({ soon, expired });
    }).catch(() => {});
  }, []);

  // Opt-in daily-check reminder: only when the user enabled it AND granted browser permission.
  useEffect(() => {
    if (!user?.daily_check_reminders || pendingIds.length === 0) return;
    if (!("Notification" in window) || Notification.permission !== "granted") return;
    const today = new Date().toLocaleDateString("en-CA");
    if (localStorage.getItem("safebook_reminder_day") === today) return;
    localStorage.setItem("safebook_reminder_day", today);
    try {
      new Notification("SafeBook — Daily checks due", {
        body: `${pendingIds.length} attraction${pendingIds.length > 1 ? "s" : ""} still need today's safety check.`,
        icon: "/icon-192.png",
      });
    } catch {}
  }, [user, pendingIds.length]);

  const manualSync = async () => { setSyncing(true); try { await syncPending(); await load(); await refreshChecks(); } finally { setSyncing(false); } };
  const hasInflatable = attractions.some((a) => a.category === "inflatable");
  const showWind = hasInflatable || user?.show_wind_check;
  const dhAttractions = attractions.filter((a) => a.category === "inflatable" && a.delivery_handovers_enabled && a.status !== "out_of_service");
  const openDelivery = () => { if (dhAttractions.length === 1) nav(`/attraction/${dhAttractions[0].id}/delivery`); else setDhOpen(true); };
  const sorted = [...attractions].sort((x, y) => Number(doneIds.includes(x.id)) - Number(doneIds.includes(y.id)));

  return (
    <div className="screen fade-up">
      <div style={{ color: C.muted, fontSize: 15 }}>{t("dashboard.welcomeBack")}</div>
      <div className="display" style={{ color: C.text, fontSize: 28, fontWeight: 900 }}>{user?.company_name || user?.display_name}</div>
      <div style={{ color: C.amber, fontSize: 12, fontWeight: 800, letterSpacing: 1, marginTop: 2 }}>{user?.role?.toUpperCase()}</div>

      {notice?.active && !!notice.message && (
        <div data-testid="global-notice" style={{ display: "flex", alignItems: "flex-start", gap: S.sm, background: C.blue, borderRadius: R.md, padding: S.md, marginTop: S.md }}>
          <Icon name="warning" size={20} color={C.black} />
          <span style={{ color: C.black, fontWeight: 800, fontSize: 14, lineHeight: 1.4 }}>{notice.message}</span>
        </div>
      )}

      <InstallPrompt />

      {!isOnline && (
        <div data-testid="offline-indicator" style={{ display: "flex", alignItems: "center", gap: S.sm, background: C.red, borderRadius: R.md, padding: S.md, marginTop: S.md }}>
          <Icon name="cloud-offline" size={18} color={C.text} />
          <span style={{ color: C.text, fontWeight: 800, fontSize: 14 }}>{t("dashboard.offlineMode")}</span>
        </div>
      )}

      {user?.role === "owner" && !user?.is_exempt_from_billing && user?.subscription_status === "trialing" && (
        <div data-testid="trial-banner" className="pressable" onClick={() => nav("/paywall")} style={{ display: "flex", alignItems: "center", gap: S.sm, background: C.amber, borderRadius: R.md, padding: S.md, marginTop: S.md, cursor: "pointer" }}>
          <Icon name="gift" size={20} color={C.black} />
          <span style={{ color: C.black, fontWeight: 800, fontSize: 14, flex: 1 }}>{t("dashboard.freeTrial")} · {user?.days_remaining ?? 0} {(user?.days_remaining ?? 0) === 1 ? t("dashboard.day") : t("dashboard.days")} {t("dashboard.leftChoosePlan")}</span>
          <Icon name="chevron-forward" size={18} color={C.black} />
        </div>
      )}
      {user?.role === "owner" && !user?.is_exempt_from_billing && user?.subscription_status === "expired" && (
        <div data-testid="expired-banner" className="pressable" onClick={() => nav("/paywall")} style={{ display: "flex", alignItems: "center", gap: S.sm, background: C.red, borderRadius: R.md, padding: S.md, marginTop: S.md, cursor: "pointer" }}>
          <Icon name="lock-closed" size={20} color={C.text} />
          <span style={{ color: C.text, fontWeight: 800, fontSize: 14, flex: 1 }}>{t("dashboard.accessEnded")}</span>
          <Icon name="chevron-forward" size={18} color={C.text} />
        </div>
      )}

      <button data-testid="sync-now-btn" className="pressable" onClick={manualSync} disabled={syncingNow}
        style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: S.sm, width: "100%", minHeight: 48, borderRadius: R.md, marginTop: S.md, border: `1px solid ${queued > 0 ? C.amber : C.borderStrong}`, background: queued > 0 ? C.amber : C.card, color: queued > 0 ? C.black : C.amber, fontWeight: 800, fontSize: 15 }}>
        <Icon name="sync" size={18} color={queued > 0 ? C.black : C.amber} />
        {syncingNow ? t("dashboard.syncing") : queued > 0 ? `${t("dashboard.syncNow")} (${queued} ${t("dashboard.pending")})` : t("dashboard.syncNow")}
      </button>

      {queued > 0 && (
        <div data-testid="offline-sync-banner" style={{ display: "flex", alignItems: "center", gap: S.sm, background: C.card2, borderRadius: R.md, padding: S.md, marginTop: S.md, border: `1px solid ${C.borderStrong}` }}>
          <Icon name="cloud-offline" size={18} color={C.text} />
          <span style={{ color: C.text, fontWeight: 700, fontSize: 13 }}>{queued} {queued > 1 ? t("dashboard.items") : t("dashboard.item")} {t("dashboard.savedOffline")}</span>
        </div>
      )}

      {pendingIds.length > 0 && (
        <div data-testid="reminder-banner" style={{ display: "flex", alignItems: "center", gap: S.sm, background: C.amber, borderRadius: R.md, padding: S.md, marginTop: S.md }}>
          <Icon name="alarm" size={20} color={C.black} />
          <span style={{ color: C.black, fontWeight: 800, fontSize: 14 }}>{pendingIds.length} {pendingIds.length > 1 ? t("dashboard.attractionsWord") : t("dashboard.attraction")} {t("dashboard.stillNeedCheck")}</span>
        </div>
      )}

      {annualDue.map((x) => {
        const overdue = x.days < 0;
        const label = overdue
          ? `${t("dashboard.overdueBy")} ${Math.abs(x.days)} ${Math.abs(x.days) === 1 ? t("dashboard.day") : t("dashboard.days")}`
          : x.days === 0 ? t("dashboard.dueToday")
          : `${t("dashboard.dueIn")} ${x.days} ${x.days === 1 ? t("dashboard.day") : t("dashboard.days")}`;
        return (
          <div key={`an-${x.id}`} data-testid={`annual-reminder-${x.id}`} className="pressable" onClick={() => nav(`/attraction/${x.id}`)} style={{ display: "flex", alignItems: "center", gap: S.sm, background: overdue ? C.red : C.blue, borderRadius: R.md, padding: S.md, marginTop: S.md, cursor: "pointer" }}>
            <Icon name="calendar" size={20} color={C.black} />
            <span style={{ color: C.black, fontWeight: 700, fontSize: 14, flex: 1 }}>{t("dashboard.annualInspectionFor")} <b>{x.name}</b> {t("dashboard.annualIs")} {label}</span>
            <Icon name="chevron-forward" size={18} color={C.black} />
          </div>
        );
      })}

      {(docRenew.soon + docRenew.expired) > 0 && (
        <div data-testid="doc-expiry-badge" className="pressable" onClick={() => nav("/attractions")} style={{ display: "flex", alignItems: "center", gap: S.sm, background: docRenew.expired > 0 ? C.red : C.amber, borderRadius: R.md, padding: S.md, marginTop: S.md, cursor: "pointer" }}>
          <Icon name="document-text" size={20} color={docRenew.expired > 0 ? C.text : C.black} />
          <span style={{ color: docRenew.expired > 0 ? C.text : C.black, fontWeight: 800, fontSize: 14, flex: 1 }}>
            {docRenew.expired > 0 ? `${docRenew.expired} ${docRenew.expired > 1 ? t("dashboard.docsWord") : t("dashboard.docWord")} ${t("dashboard.expired")}` : ""}
            {docRenew.expired > 0 && docRenew.soon > 0 ? " · " : ""}
            {docRenew.soon > 0 ? `${docRenew.soon} ${t("dashboard.expiringSoon")}` : ""}{" — "}{t("dashboard.tapToRenew")}
          </span>
          <Icon name="chevron-forward" size={18} color={docRenew.expired > 0 ? C.text : C.black} />
        </div>
      )}

      {showWind && (
        <div data-testid="quick-wind-check-btn" className="pressable" onClick={() => nav("/wind-check")} style={{ marginTop: S.lg, background: C.amber, borderRadius: R.lg, padding: S.lg, display: "flex", alignItems: "center", gap: S.md, minHeight: 96, cursor: "pointer" }}>
          <Icon name="flash" size={40} color={C.black} />
          <div style={{ flex: 1 }}>
            <div className="display" style={{ color: C.black, fontSize: 22, fontWeight: 900 }}>{t("dashboard.quickWindCheck")}</div>
            <div style={{ color: C.black, fontSize: 14, fontWeight: 600, opacity: 0.8 }}>{t("dashboard.logWindInstantly")}</div>
          </div>
          <Icon name="chevron-forward" size={30} color={C.black} />
        </div>
      )}

      {dhAttractions.length > 0 && (
        <div data-testid="dashboard-delivery-btn" className="pressable" onClick={openDelivery} style={{ marginTop: S.md, background: C.blue, borderRadius: R.lg, padding: S.lg, display: "flex", alignItems: "center", gap: S.md, minHeight: 88, cursor: "pointer" }}>
          <Icon name="clipboard-outline" size={36} color={C.black} />
          <div style={{ flex: 1 }}>
            <div className="display" style={{ color: C.black, fontSize: 20, fontWeight: 900 }}>{t("dashboard.deliveryHandover")}</div>
            <div style={{ color: C.black, fontSize: 13, fontWeight: 600, opacity: 0.8 }}>{t("dashboard.deliverySub")}</div>
          </div>
          <Icon name="chevron-forward" size={28} color={C.black} />
        </div>
      )}

      <div style={{ display: "flex", gap: S.md, marginTop: S.lg }}>
        {[[t("dashboard.attractions"), attractions.length], [t("dashboard.checksToday"), todayCount]].map(([lbl, num]) => (
          <div key={lbl} style={{ flex: 1, background: C.card, borderRadius: R.md, padding: S.lg, border: `1px solid ${C.border}` }}>
            <div className="display" style={{ color: C.text, fontSize: 32, fontWeight: 900 }}>{num}</div>
            <div style={{ color: C.muted, fontSize: 13, marginTop: 2 }}>{lbl}</div>
          </div>
        ))}
      </div>

      <div data-testid="open-reports-center" className="pressable" onClick={() => nav("/reports")} style={{ ...rowCard, marginTop: S.lg }}>
        <div style={{ width: 44, height: 44, borderRadius: 10, background: C.card2, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Icon name="stats-chart" size={22} color={C.amber} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ color: C.text, fontSize: 17, fontWeight: 800 }}>{t("dashboard.reportsCenter")}</div>
          <div style={{ color: C.muted, fontSize: 12, marginTop: 2 }}>{t("dashboard.reportsCenterSub")}</div>
        </div>
        <Icon name="chevron-forward" size={22} color={C.muted} />
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: S.xl, marginBottom: S.md }}>
        <div className="display" style={{ color: C.text, fontSize: 18, fontWeight: 800 }}>{t("dashboard.todaysChecks")}</div>
        {checkable.length > 0 && <div style={{ color: C.amber, fontWeight: 800, fontSize: 14 }}>{doneIds.length}/{checkable.length} {t("dashboard.done")}</div>}
      </div>

      {attractions.length > 0 && (
        <button data-testid="backdate-open-btn" className="pressable" onClick={() => { setBackDate(""); setBackOpen(true); }}
          style={{ display: "flex", alignItems: "center", gap: S.sm, width: "100%", minHeight: 46, borderRadius: R.md, marginBottom: S.md, border: `1px dashed ${C.borderStrong}`, background: C.card2, color: C.amber, fontWeight: 800, fontSize: 14, padding: `0 ${S.md}px` }}>
          <Icon name="calendar" size={18} color={C.amber} />
          <span style={{ flex: 1, textAlign: "left" }}>{t("dashboard.missedDay")}</span>
          <Icon name="chevron-forward" size={18} color={C.muted} />
        </button>
      )}

      {loading ? (
        <div style={{ color: C.amber, textAlign: "center", marginTop: S.xl }}>{t("actions.loading")}</div>
      ) : attractions.length === 0 ? (
        <div style={{ textAlign: "center", padding: `${S.xxl}px 0`, color: C.muted }}>
          <Icon name="clipboard-outline" size={48} color={C.muted} />
          <div style={{ color: C.text, fontSize: 17, fontWeight: 700, marginTop: S.md }}>{t("dashboard.noAttractions")}</div>
          <div style={{ marginTop: 4 }}>{t("dashboard.addFromTab")}</div>
        </div>
      ) : sorted.map((a) => {
        const cateringA = a.category === "catering";
        const done = doneIds.includes(a.id);
        return (
          <div key={a.id} data-testid={`inspect-attraction-${a.id}`} className="pressable" onClick={() => (cateringA ? setCateringPick(a) : nav(`/inspect/${a.id}`))} style={{ ...rowCard, borderColor: done ? C.green : C.border, opacity: done ? 0.85 : 1, cursor: "pointer" }}>
            <div style={{ width: 44, height: 44, borderRadius: 10, background: catColor(a.category), display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Icon name={catIcon(a.category)} size={22} color={C.black} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ color: C.text, fontSize: 17, fontWeight: 800 }}>{a.name}</div>
              {a.status === "out_of_service" ? (
                <div data-testid={`oos-${a.id}`} style={{ display: "inline-flex", alignItems: "center", gap: 4, background: C.red, padding: "3px 8px", borderRadius: R.sm, marginTop: 4 }}>
                  <Icon name="ban" size={12} color={C.text} /><span style={{ color: C.text, fontWeight: 900, fontSize: 11, letterSpacing: 0.5 }}>{t("status.outOfService")}</span>
                </div>
              ) : cateringA ? (
                <div style={{ color: "#f97316", fontSize: 12.5, fontWeight: 700, marginTop: 2 }}>{t("dashboard.cateringUnitTap")}</div>
              ) : done ? (
                <div data-testid={`check-by-${a.id}`} style={{ color: C.green, fontSize: 12.5, fontWeight: 700, marginTop: 2 }}>{t("dashboard.checkedToday")} · {checks[a.id]?.by || "—"}{checks[a.id]?.at ? ` · ${fmtCheckTime(checks[a.id].at)}` : ""}</div>
              ) : (
                <div style={{ color: C.muted, fontSize: 13, marginTop: 2 }}>{a.category === "inflatable" ? t("dashboard.inflatable") : t("dashboard.ride")} · {a.checklist.length} {t("dashboard.checks")}</div>
              )}
            </div>
            {cateringA ? (
              <div data-testid={`catering-action-${a.id}`} style={{ display: "flex", alignItems: "center", gap: 4, background: "#f97316", padding: "8px 12px", borderRadius: R.pill }}>
                <Icon name="restaurant" size={16} color={C.black} /><span style={{ color: C.black, fontWeight: 800 }}>{t("dashboard.foodSafety")}</span>
              </div>
            ) : done ? (
              <div data-testid={`status-done-${a.id}`} style={{ display: "flex", alignItems: "center", gap: 4, background: "rgba(16,185,129,0.15)", padding: "8px 12px", borderRadius: R.pill, border: `1px solid ${C.green}` }}>
                <Icon name="checkmark-circle" size={16} color={C.green} /><span style={{ color: C.green, fontWeight: 800 }}>{t("dashboard.checked")}</span>
              </div>
            ) : (
              <div data-testid={`status-due-${a.id}`} style={{ display: "flex", alignItems: "center", gap: 4, background: C.green, padding: "8px 12px", borderRadius: R.pill }}>
                <Icon name="play" size={18} color={C.black} /><span style={{ color: C.black, fontWeight: 800 }}>{t("dashboard.start")}</span>
              </div>
            )}
          </div>
        );
      })}
      {backOpen && (
        <div className="modal-backdrop modal-center" onClick={() => setBackOpen(false)}>
          <div data-testid="backdate-modal" onClick={(e) => e.stopPropagation()} style={{ background: C.card, borderRadius: R.lg, padding: S.xl, width: "100%", maxWidth: 420, border: `1px solid ${C.borderStrong}`, maxHeight: "88vh", overflowY: "auto" }}>
            <div style={{ display: "flex", alignItems: "center", gap: S.sm, marginBottom: S.sm }}>
              <Icon name="calendar" size={24} color={C.amber} />
              <div className="display" style={{ color: C.text, fontSize: 22, fontWeight: 900, flex: 1 }}>{t("dashboard.logMissedCheck")}</div>
              <button data-testid="backdate-close" className="pressable" onClick={() => setBackOpen(false)} style={{ background: "transparent", border: "none", color: C.muted, fontSize: 24, fontWeight: 900, lineHeight: 1 }}>×</button>
            </div>
            <div style={{ color: C.sub, fontSize: 14, lineHeight: 1.4, marginBottom: S.md }}>{t("dashboard.backdateHelp")}</div>
            <div style={{ color: C.sub, fontSize: 13, fontWeight: 700, marginBottom: S.xs, textTransform: "uppercase" }}>{t("dashboard.checkDate")}</div>
            <input data-testid="backdate-date-input" type="date" value={backDate} min={minBackStr} max={todayStr} onChange={(e) => setBackDate(e.target.value)}
              style={{ background: C.card2, borderRadius: R.md, border: `1px solid ${C.borderStrong}`, color: C.text, fontSize: 16, padding: `0 ${S.md}px`, minHeight: 52, width: "100%", marginBottom: S.md, outline: "none" }} />
            {backDate && (
              <>
                <div style={{ color: C.text, fontSize: 15, fontWeight: 800, marginBottom: S.sm }}>{t("dashboard.chooseAttractionFor")} {formatYmd(backDate)}</div>
                {attractions.filter((a) => a.category !== "catering").map((a) => (
                  <button key={a.id} data-testid={`backdate-attraction-${a.id}`} className="pressable" onClick={() => nav(`/inspect/${a.id}?check_date=${backDate}`)}
                    style={{ display: "flex", alignItems: "center", gap: S.md, width: "100%", background: C.card2, borderRadius: R.md, padding: S.md, marginBottom: S.sm, border: `1px solid ${C.border}`, textAlign: "left" }}>
                    <div style={{ width: 38, height: 38, borderRadius: 9, background: a.category === "inflatable" ? C.blue : C.amber, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <Icon name={a.category === "inflatable" ? "balloon" : "cog"} size={20} color={C.black} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ color: C.text, fontSize: 15, fontWeight: 800 }}>{a.name}</div>
                      <div style={{ color: C.muted, fontSize: 12 }}>{a.category === "inflatable" ? t("dashboard.inflatable") : t("dashboard.ride")} · {a.checklist.length} {t("dashboard.checks")}</div>
                    </div>
                    <Icon name="chevron-forward" size={18} color={C.muted} />
                  </button>
                ))}
              </>
            )}
          </div>
        </div>
      )}
      {dhOpen && (
        <div className="modal-backdrop modal-center" onClick={() => setDhOpen(false)}>
          <div data-testid="delivery-picker-modal" onClick={(e) => e.stopPropagation()} style={{ background: C.card, borderRadius: R.lg, padding: S.xl, width: "100%", maxWidth: 420, border: `1px solid ${C.borderStrong}`, maxHeight: "88vh", overflowY: "auto" }}>
            <div style={{ display: "flex", alignItems: "center", gap: S.sm, marginBottom: S.sm }}>
              <Icon name="clipboard-outline" size={24} color={C.blue} />
              <div className="display" style={{ color: C.text, fontSize: 22, fontWeight: 900, flex: 1 }}>{t("handover.title")}</div>
              <button data-testid="delivery-picker-close" className="pressable" onClick={() => setDhOpen(false)} style={{ background: "transparent", border: "none", color: C.muted, fontSize: 24, fontWeight: 900, lineHeight: 1 }}>×</button>
            </div>
            <div style={{ color: C.sub, fontSize: 14, lineHeight: 1.4, marginBottom: S.md }}>{t("dashboard.deliveryPickerHelp")}</div>
            {dhAttractions.map((a) => (
              <button key={a.id} data-testid={`delivery-pick-${a.id}`} className="pressable" onClick={() => nav(`/attraction/${a.id}/delivery`)}
                style={{ display: "flex", alignItems: "center", gap: S.md, width: "100%", background: C.card2, borderRadius: R.md, padding: S.md, marginBottom: S.sm, border: `1px solid ${C.border}`, textAlign: "left" }}>
                <div style={{ width: 38, height: 38, borderRadius: 9, background: C.blue, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Icon name="balloon" size={20} color={C.black} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ color: C.text, fontSize: 15, fontWeight: 800 }}>{a.name}</div>
                  <div style={{ color: C.muted, fontSize: 12 }}>{t("dashboard.inflatable")} · {t("handover.title")}</div>
                </div>
                <Icon name="chevron-forward" size={18} color={C.muted} />
              </button>
            ))}
          </div>
        </div>
      )}
      {cateringPick && (
        <div className="modal-backdrop modal-center" onClick={() => setCateringPick(null)}>
          <div data-testid="catering-check-modal" onClick={(e) => e.stopPropagation()} style={{ background: C.card, borderRadius: R.lg, padding: S.xl, width: "100%", maxWidth: 420, border: `1px solid ${C.borderStrong}`, maxHeight: "88vh", overflowY: "auto" }}>
            <div style={{ display: "flex", alignItems: "center", gap: S.sm, marginBottom: S.sm }}>
              <Icon name="restaurant" size={24} color="#f97316" />
              <div className="display" style={{ color: C.text, fontSize: 22, fontWeight: 900, flex: 1 }}>{t("dashboard.foodSafetyCheck")}</div>
              <button data-testid="catering-check-close" className="pressable" onClick={() => setCateringPick(null)} style={{ background: "transparent", border: "none", color: C.muted, fontSize: 24, fontWeight: 900, lineHeight: 1 }}>×</button>
            </div>
            <div style={{ color: C.sub, fontSize: 14, lineHeight: 1.4, marginBottom: S.md }}>{t("dashboard.whichCheckPre")} <b style={{ color: C.text }}>{cateringPick.name}</b>{t("dashboard.whichCheckPost")}</div>
            {CATERING_SESSIONS.map((s2) => (
              <button key={s2.key} data-testid={`catering-session-${s2.key}`} className="pressable" onClick={() => nav(`/attraction/${cateringPick.id}/food-safety?session=${s2.key}`)}
                style={{ display: "flex", alignItems: "center", gap: S.md, width: "100%", background: C.card2, borderRadius: R.md, padding: S.md, marginBottom: S.sm, border: `1px solid ${C.border}`, textAlign: "left" }}>
                <div style={{ width: 44, height: 44, borderRadius: 11, background: s2.color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon name={s2.icon} size={22} color={C.black} /></div>
                <div style={{ flex: 1 }}>
                  <div style={{ color: C.text, fontSize: 16, fontWeight: 800 }}>{s2.label}</div>
                  <div style={{ color: C.muted, fontSize: 12 }}>{CATERING_HINTS[s2.key]}</div>
                </div>
                <Icon name="chevron-forward" size={18} color={C.muted} />
              </button>
            ))}
            <button data-testid="catering-session-delivery" className="pressable" onClick={() => nav(`/attraction/${cateringPick.id}/catering-delivery`)}
              style={{ display: "flex", alignItems: "center", gap: S.md, width: "100%", background: C.card2, borderRadius: R.md, padding: S.md, marginBottom: S.sm, border: `1px solid ${C.border}`, textAlign: "left" }}>
              <div style={{ width: 44, height: 44, borderRadius: 11, background: "#0ea5e9", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon name="cube" size={22} color={C.black} /></div>
              <div style={{ flex: 1 }}>
                <div style={{ color: C.text, fontSize: 16, fontWeight: 800 }}>{t("dashboard.deliveryLog")}</div>
                <div style={{ color: C.muted, fontSize: 12 }}>{t("dashboard.deliveryLogSub")}</div>
              </div>
              <Icon name="chevron-forward" size={18} color={C.muted} />
            </button>
            <button data-testid="catering-session-cleaning" className="pressable" onClick={() => nav(`/attraction/${cateringPick.id}/cleaning`)}
              style={{ display: "flex", alignItems: "center", gap: S.md, width: "100%", background: C.card2, borderRadius: R.md, padding: S.md, marginBottom: S.sm, border: `1px solid ${C.border}`, textAlign: "left" }}>
              <div style={{ width: 44, height: 44, borderRadius: 11, background: "#06b6d4", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon name="sparkles" size={22} color={C.black} /></div>
              <div style={{ flex: 1 }}>
                <div style={{ color: C.text, fontSize: 16, fontWeight: 800 }}>{t("dashboard.cleaningChecklist")}</div>
                <div style={{ color: C.muted, fontSize: 12 }}>{t("dashboard.cleaningChecklistSub")}</div>
              </div>
              <Icon name="chevron-forward" size={18} color={C.muted} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
